import React, { useId } from 'react';
import styled from '@emotion/styled';
import { overflow, position, width } from 'styled-system';

import InnerInput from './InnerInput';
import Label, { applyActiveLabelCSS, applyLabelCSS, labelActiveCSS, labelFocusCSS } from './Label';
import StateMessages from './StateMessages';
import type { InputProps } from './types';

const EMPTY_LIST = [];

export const Wrapper = styled.div`
  ${position};
  ${width};
  ${overflow};

  > label:not(.label-wrapper) {
    ${applyLabelCSS};
    ${applyActiveLabelCSS};
  }

  &:focus-within label:not(.label-wrapper) {
    ${labelActiveCSS};
    ${labelFocusCSS};
  }
`;

const wrapperStyles = {
  position: 'relative',
  width: 1,
};

const Input = ({
  label,
  inputRef,
  errors = EMPTY_LIST,
  warnings = EMPTY_LIST,
  successes = EMPTY_LIST,
  messages = EMPTY_LIST,
  withIcon,
  withMessagesIcon,
  onChange,
  labelStyles,
  ...restProps
}: InputProps) => {
  const {
    id: initialId,
    type,
    value,
    disabled,
    readOnly,
    hasError,
    hasWarning,
    hasSuccess,
    isLoading,
    width: inputWidth,
  } = restProps;
  const generatedId = useId();
  const id = initialId ?? generatedId;
  const wrapperProps = {
    ...wrapperStyles,
    type,
    value,
    disabled,
    hasError,
    readOnly,
    hasWarning,
    hasSuccess,
    isLoading,
    ...(inputWidth && { width: inputWidth }),
  };

  const stateMessagesId = `${id}-state-messages`;

  return (
    <Wrapper {...wrapperProps} data-testid="input-wrapper">
      <InnerInput
        id={id}
        ref={inputRef}
        onChange={onChange}
        value={value}
        {...(hasError
          ? {
              'aria-invalid': true,
            }
          : {})}
        aria-describedby={stateMessagesId}
        disabled={disabled}
        readOnly={readOnly}
        aria-disabled={readOnly}
        {...restProps}
      />
      {label && (
        <Label htmlFor={id} {...labelStyles}>
          {label}
        </Label>
      )}
      <StateMessages
        id={stateMessagesId}
        withIcon={withIcon}
        withMessagesIcon={withMessagesIcon}
        errors={errors}
        warnings={warnings}
        successes={successes}
        messages={messages}
        hasError={hasError}
        hasWarning={hasWarning}
        hasSuccess={hasSuccess}
        isLoading={isLoading}
      />
    </Wrapper>
  );
};

export default Input;
